import {
	AfterViewInit,
	Component,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	signal,
	SimpleChanges,
	WritableSignal,
	ChangeDetectionStrategy,
	inject,
	input,
	output,
	viewChild,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { Icons } from '../../../util/Icons';
import { Floor } from '@eadsysteme/ewsrealestateclient-v2';
import { DateUtil } from '../../../util/DateUtil';
import { ArticlePropertyLocalisationService } from '../../../services/article-property-localisation.service';
import { MatFormField, MatOption, MatSelect } from '@angular/material/select';
import { Config } from '../../../config';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatLabel } from '@angular/material/form-field';
import FloorTypeEnum = Floor.FloorTypeEnum;

@Component({
	selector: 'ara-search-select',
	templateUrl: './ara-search-select.component.html',
	styleUrls: ['./ara-search-select.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		MatFormField,
		TranslocoDirective,
		MatProgressSpinner,
		MatSelect,
		ReactiveFormsModule,
		MatOption,
		NgxMatSelectSearchModule,
		MatIcon,
		MatIconButton,
		MatTooltip,
		MatLabel,
	],
})
export class AraSearchSelectComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
	private translocoService = inject(TranslocoService);
	icons = inject(Icons);
	private dateUtil = inject(DateUtil);
	private articlePropertyLocalisation = inject(ArticlePropertyLocalisationService);

	floorTypes: string[] = Object.values(FloorTypeEnum);
	/** display options only after a specific length of input characters to search */
	readonly minimumSearchLength = input(0);
	/** list of data */
	@Input() data: any[] = [];
	/** Custom Stylings */
	readonly customStyle = input('');
	readonly disabled = input(false);
	/** translation enum prefix for uppercase values  */
	readonly translationEnumPrefix = input('enum.');
	readonly skipEnumTranslation = input(false);
	/* Show Mat-Header */
	readonly label = input('');
	/** control for the selected  for multi-selection */
	public readonly finishedLoading = input(signal(true));
	public readonly dataMultiCtrl = input<UntypedFormControl>(
		new UntypedFormControl({
			value: '',
			disabled: !this.finishedLoading(),
		})
	);
	/** control for the MatSelect filter keyword multi-selection */
	public dataMultiFilterCtrl: UntypedFormControl = new UntypedFormControl();
	/** list of s filtered by search keyword */
	public filteredDataMulti: WritableSignal<any[]> = signal([]);
	public readonly clearFilter = input<Subject<boolean>>(new Subject<boolean>());
	public readonly preSetCheckedData = input<Subject<any[]>>(new Subject<any[]>());
	/** flags to set the toggle all checkbox state */
	isIndeterminate = false;
	isChecked = false;
	readonly multiSelect = viewChild<MatSelect>('multiSelect');
	readonly newFilters = output<any[]>();
	/** local copy of filtered s to help set the toggle all checkbox state */
	protected filteredDataCache: any[] = [];
	/** Subject that emits when the component has been destroyed. */
	protected _onDestroy = new Subject<void>();
	private dataChanged: boolean;
	private dataIsSelected = false;
	customClass = 'col-span-8';
	customClassIfFilterSelected = 'col-span-7';
	private searchSubject: Subject<string> = new Subject<string>();
	searchTimeoutDelay = Config.SEARCH_DELAY;

	isUpperCase = (string) => /^[A-Z_]*[A-Z_]*$/.test(string);

	// executed when opening the filter dropdown
	ngOnChanges(changes: SimpleChanges): void {
		// let change = changes['data'];
		if (!this.data) {
			this.data = [];
		}

		// only show options if data is pre-set or the minimum search length is met
		const minimumSearchLength = this.minimumSearchLength();
		if (minimumSearchLength === 0 || this.dataIsSelected || minimumSearchLength <= this.dataMultiFilterCtrl.value?.trim().length) {
			this.filteredDataMulti.set(this.data.slice());
		} else {
			this.filteredDataMulti.set([]);
		}
	}

	ngOnInit() {
		// set initial selection
		const dataMultiCtrl = this.dataMultiCtrl();
		if (!dataMultiCtrl.value) {
			dataMultiCtrl.setValue([]);
		}
		const disabled = this.disabled();
		if (disabled) {
			dataMultiCtrl.disable({ onlySelf: disabled, emitEvent: disabled });
		}

		const preSetCheckedData = this.preSetCheckedData();
		if (preSetCheckedData) {
			preSetCheckedData.subscribe((value) => {
				this.dataIsSelected = true;
				this.data = value;
				this.dataMultiCtrl().setValue(value);
			});
		}

		// load the initial list
		const minimumSearchLength = this.minimumSearchLength();
		if (minimumSearchLength === 0 || this.dataIsSelected || minimumSearchLength <= this.dataMultiFilterCtrl.value?.trim().length) {
			this.filteredDataMulti.set(this.data.slice());
		} else {
			this.filteredDataMulti.set([]);
		}

		// listen for search field value changes
		this.dataMultiFilterCtrl.valueChanges
			.pipe(
				takeUntil(this._onDestroy),
				debounceTime(this.searchTimeoutDelay), // Wartezeit nach der letzten Eingabe
				distinctUntilChanged()
			) // Nur fortfahren, wenn sich der Wert ändert)
			.subscribe(() => {
				this.filterDataMulti();
				this.setToggleAllCheckboxState();
			});

		// listen for multi select field value changes
		dataMultiCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => {
			this.setToggleAllCheckboxState();
			this.dataChanged = true;
			// this.newFilters.emit(this.dataMultiCtrl.value);
		});

		this.clearFilter()
			.pipe(takeUntil(this._onDestroy))
			.subscribe((value) => {
				if (value && this.dataMultiCtrl().value.length > 0) {
					this.clearSelection();
				}
			});
	}

	ngAfterViewInit() {
		this.setInitialValue();
	}

	ngOnDestroy() {
		this._onDestroy.next();
		this._onDestroy.complete();
	}

	toggleSelectAll(selectAllValue: boolean) {
		if (selectAllValue) {
			this.dataMultiCtrl().patchValue(this.filteredDataMulti());
		} else {
			this.dataMultiCtrl().patchValue([]);
		}
	}

	translate(key: string): string {
		return this.translocoService.translate(key);
	}

	beautifyOption(data: any) {
		if (Number.isInteger(data)) {
			return data + '';
		} else {
			if (!data) {
				return data;
			}
			data = data.trim();
			if (data === '' || data.indexOf(' ') !== -1) {
				return data;
			}

			if (this.isUpperCase(data)) {
				// replace possible enums
				if (this.skipEnumTranslation()) {
					return data;
				}
				const translation = this.translate(this.translationEnumPrefix() + data);
				// fallback for uppercase values like manufacturer id (MAD, EFE, ...) if not skipped already
				return translation.replace(this.translationEnumPrefix(), '');
			} else if (data.startsWith('{') && data.endsWith('}')) {
				return this.articlePropertyLocalisation.localizeCustomSerializedName(data);
			} else if (data === 'false' || data === 'true') {
				return this.translate('common.' + data);
			}
			return data;
		}
	}

	clearSelection() {
		this.dataMultiCtrl().setValue([]);
		this.newFilters.emit(this.dataMultiCtrl().value);
	}

	isFilterSelected(): boolean {
		return this.dataMultiCtrl()?.value?.length > 0;
	}

	emitFiltersWhenClosed($event: boolean) {
		if ($event === false) {
			if (this.dataChanged) {
				this.newFilters.emit(this.dataMultiCtrl().value);
				this.dataChanged = false;
			}
		}
	}

	/**
	 * Sets the initial value after the filtereds are loaded initially
	 */
	protected setInitialValue() {
		// Intentionally empty: this component needs no special initial-value handling;
		// kept as an override hook for subclasses (mirrors ngx-mat-select-search).
	}

	protected filterDataMulti() {
		if (!this.data) {
			return;
		}
		// get the search keyword
		let search = this.dataMultiFilterCtrl.value?.trim();
		if (!search) {
			// only show data if search input isn't required to have a minimum length or if data is selected
			if (this.minimumSearchLength() === 0 || this.dataIsSelected) {
				this.filteredDataCache = this.data.slice();
			} else {
				this.filteredDataCache = [];
			}

			this.filteredDataMulti.set(this.filteredDataCache);
			return;
		} else {
			search = search.toLowerCase();
		}
		// filter the data
		this.filteredDataCache = this.data.filter((data) => {
			if (search.length < this.minimumSearchLength() && !this.dataIsSelected) {
				return false; // don't show any data if too few search chars given and if no data is selected yet
			}
			if (!data) {
				return !search; // show empty filter row(s) in case of empty search text
			}
			if (Number.isInteger(data)) {
				return (data + '').indexOf(search) > -1;
			} else {
				if (this.isUpperCase(data)) {
					// replace possible enums
					if (this.skipEnumTranslation()) {
						return data;
					}
					let translation = this.translate(this.translationEnumPrefix() + data).toLowerCase();
					// fallback for uppercase values like manufacturer id (MAD, EFE, ...) if not skipped already
					translation = translation.replace(this.translationEnumPrefix(), '');
					return translation.indexOf(search) > -1;
				} else {
					return data.toLowerCase().indexOf(search) > -1;
				}
			}
		});
		this.filteredDataMulti.set(this.filteredDataCache);
	}

	protected setToggleAllCheckboxState() {
		let filteredLength = 0;
		const dataMultiCtrl = this.dataMultiCtrl();
		if (dataMultiCtrl && dataMultiCtrl.value) {
			this.filteredDataCache.forEach((el) => {
				if (this.dataMultiCtrl().value.indexOf(el) > -1) {
					filteredLength++;
				}
			});
			this.isIndeterminate = filteredLength > 0 && filteredLength < this.filteredDataCache.length;
			this.isChecked = filteredLength > 0 && filteredLength === this.filteredDataCache.length;
		}
		this.dataIsSelected = filteredLength > 0;
	}
}
